import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation } from "@tanstack/react-router";
import { en, type MessageKey } from "./messages/en";
import { bg } from "./messages/bg";

export type Locale = "en" | "bg";
const COOKIE = "perceptica_locale";
const DICTIONARIES: Record<Locale, Record<MessageKey, string>> = { en, bg };

/**
 * Locale slug map.
 *
 * Most routes share the same slug across locales (e.g. /about-us <-> /bg/about-us)
 * and don't need to be listed here. This map covers the routes whose BG slug
 * differs from the EN slug, plus EN aliases that point to a BG-only localized
 * slug. Keys/values are language-agnostic paths (no /bg prefix). If a path is
 * not listed, the switcher falls back to a 1:1 slug.
 */
const EN_TO_BG: Record<string, string> = {
  // Most routes share the same slug across locales now (canonical BG URLs
  // mirror EN under /bg). Legacy Cyrillic slugs still exist as 301 redirects,
  // but the switcher targets the canonical paths.
  // Map ONLY EN paths whose canonical BG URL has a different slug.
};

// Reverse map BG -> EN. Built once at module load. First wins, so when several
// EN aliases map to the same BG slug (e.g. request-a-demo + get-a-free-trial)
// the canonical EN path is preserved.
const BG_TO_EN: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const [enPath, bgPath] of Object.entries(EN_TO_BG)) {
    if (!(bgPath in out)) out[bgPath] = enPath;
  }
  return out;
})();

type Ctx = {
  locale: Locale;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  /** Strip the /bg prefix (if any) to get the language-agnostic path. */
  stripLocale: (pathname: string) => string;
  /** Prepend /bg if current locale is bg, otherwise return path unchanged. */
  localizePath: (path: string) => string;
};

const I18nCtx = createContext<Ctx | null>(null);

export function localeFromPath(pathname: string): Locale {
  return pathname === "/bg" || pathname.startsWith("/bg/") ? "bg" : "en";
}

/** Normalize trailing slash (except for the root "/"). */
function trimTrailingSlash(p: string): string {
  if (p.length > 1 && p.endsWith("/")) return p.slice(0, -1);
  return p;
}

export function stripLocaleFromPath(pathname: string): string {
  // Strip the /bg prefix (if any) to recover the language-agnostic EN-side path.
  if (pathname === "/bg") return "/";
  if (!pathname.startsWith("/bg/")) return pathname;
  const bgPath = pathname.slice(3) || "/";
  // If the BG path uses a localized slug, return its EN counterpart so callers
  // can re-localize to either side correctly.
  const normalized = trimTrailingSlash(bgPath);
  return BG_TO_EN[normalized] ?? bgPath;
}

export function localizePathFor(locale: Locale, path: string): string {
  // Input `path` is the EN-side language-agnostic path (output of
  // stripLocaleFromPath). Map to the locale-correct URL.
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  const normalized = trimTrailingSlash(withSlash);
  if (locale === "en") {
    // Defensive: if we received a BG-localized slug, map back to EN.
    return BG_TO_EN[normalized] ?? normalized;
  }
  if (normalized === "/" || normalized === "") return "/bg";
  // Never double-prefix: a path that already carries /bg is returned as-is.
  if (normalized === "/bg" || normalized.startsWith("/bg/")) return normalized;
  // Prefer a known EN -> BG slug mapping; otherwise assume the slug is shared.
  const bgPath = EN_TO_BG[normalized] ?? normalized;
  return `/bg${bgPath}`;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const locale = localeFromPath(location.pathname);

  // Persist choice for first-visit detection on next session.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = locale;
    document.cookie = `${COOKIE}=${locale}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
  }, [locale]);

  const t = useCallback(
    (key: MessageKey, vars?: Record<string, string | number>) => {
      const dict = DICTIONARIES[locale] ?? en;
      let value: string = dict[key] ?? en[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          value = value.replace(new RegExp(`{${k}}`, "g"), String(v));
        }
      }
      return value;
    },
    [locale],
  );

  const value = useMemo<Ctx>(
    () => ({
      locale,
      t,
      stripLocale: stripLocaleFromPath,
      localizePath: (p) => localizePathFor(locale, p),
    }),
    [locale, t],
  );

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useT() {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error("useT must be used within I18nProvider");
  return ctx;
}

/** Convenience hook for prefixing a path with /bg when current locale is bg. */
export function useLocalePath() {
  const { localizePath } = useT();
  return localizePath;
}
